import React, { useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { SingleCheckbox } from 'src/components/widgets';
import { ColumnField, FilterType } from '@rcp/types';
import { DataGrid, GridOption } from 'src/features/data-grid/data-grid';
import { localizationService } from 'src/services';
import { LocalStorageName } from '@rcp/types';
import {
	saveEmailNotificationSettings,
	toggleNotification,
	toggleAllOrganizationsForType,
	selectEmailNotificationsState,
	selectAllNotificationTypes,
	selectOriginalNotifications,
	selectFullDataset,
	selectHasUnsavedChanges
} from 'src/redux/ipp/authority/authority-account/users/email-notifications.slice';
import { emailNotificationsRestSlice } from 'src/redux/ipp/authority/authority-account/users/email-notifications.slice';
import CollapsibleCard from 'src/components/widgets/collapsible-card/collapsible-card';
import { IppConstants } from 'src/constants';
import './User-Email-Notification-Settings.scss';

interface Props {
	userProfileId: number;
}
const { queryParams } = IppConstants;
const { initialPage, shortPageSize } = queryParams;

export const UserEmailNotificationSettings: React.FC<Props> = ({ userProfileId }) => {
	const dispatch = useDispatch();

	const {
		loading,
		error,
		data: { result, page, size, total }
	} = useSelector(selectEmailNotificationsState);
	const allTypes = useSelector(selectAllNotificationTypes);
	const originalNotifications = useSelector(selectOriginalNotifications);
	const fullDataset = useSelector(selectFullDataset);
	const hasUnsavedChanges = useSelector(selectHasUnsavedChanges);

	const isColumnFullySelected = useCallback(
		(typeId: number): boolean => {
			if (!result || result.length === 0) return false;
			return result.every((org: any) => org[`type_${typeId}`] === true);
		},
		[result]
	);

	const handleColumnSelectAllChange = useCallback((e: Event) => {
		const target = e.target as HTMLInputElement;
		const typeIdAttr = target.getAttribute('data-type-id') || '';
		const typeId = parseInt(typeIdAttr, 10);
		
		if (!typeId || isNaN(typeId)) {
			return;
		}
		
		dispatch(toggleAllOrganizationsForType({ 
			typeId, 
			checked: target.checked 
		}));
	}, [dispatch]);

	useEffect(() => {
		const addSelectAllRow = () => {
			const gridTable = document.querySelector('.email-notifications-grid tbody');
			
			if (!gridTable) return;
			
			const existingRow = document.querySelector('.select-all-row');
			if (existingRow) {
				existingRow.remove();
			}

			const selectAllRow = document.createElement('tr');
			selectAllRow.className = 'select-all-row';
			
			const labelCell = document.createElement('td');
			labelCell.className = 'select-all-label';
			labelCell.innerHTML = '<strong>Select All</strong>';
			selectAllRow.appendChild(labelCell);

			const selectAllText = localizationService.getLocalizedString('grid.selectAll');
			allTypes.forEach((type: any) => {
				const checkboxCell = document.createElement('td');
				checkboxCell.className = 'text-center';

				const isChecked = isColumnFullySelected(type.emailNotificationTypeId);
				const checkboxId = `selectAllColumn_${type.emailNotificationTypeId}`;

				const wrapper = document.createElement('div');
				wrapper.className = 'custom-control custom-checkbox table-checkbox-align';

				const input = document.createElement('input');
				input.type = 'checkbox';
				input.className = 'custom-control-input column-select-all';
				input.id = checkboxId;
				input.dataset.typeId = String(type.emailNotificationTypeId);
				if (isChecked) input.checked = true;

				const label = document.createElement('label');
				label.className = 'custom-control-label table-checkbox-label';
				label.htmlFor = checkboxId;

				const srSpan = document.createElement('span');
				srSpan.className = 'sr-only';
				srSpan.textContent = `${selectAllText} - ${type.notificationType}`;

				label.appendChild(srSpan);
				wrapper.appendChild(input);
				wrapper.appendChild(label);
				checkboxCell.appendChild(wrapper);
				selectAllRow.appendChild(checkboxCell);
			});
			
			gridTable.insertBefore(selectAllRow, gridTable.firstChild);
			
			const checkboxes = selectAllRow.querySelectorAll('.column-select-all');
			checkboxes.forEach(checkbox => {
				checkbox.addEventListener('change', handleColumnSelectAllChange);
			});
		};

		addSelectAllRow();

		return () => {
			const selectAllRow = document.querySelector('.select-all-row');
			if (selectAllRow) {
				const checkboxes = selectAllRow.querySelectorAll('.column-select-all');
				checkboxes.forEach(checkbox => {
					checkbox.removeEventListener('change', handleColumnSelectAllChange);
				});
			}
		};
	}, [result, allTypes, handleColumnSelectAllChange, isColumnFullySelected]);

	const renderIndustryName = useCallback(
		(props: any) => (
			<td className="text-center">{props.dataItem.industryName || 'N/A'}</td>
		),
		[]
	);

	const renderCheckbox = useCallback(
		(typeId: number, typeName: string) => (props: any) => {
			const orgId = props.dataItem.organizationId;
			const checked = props.dataItem[`type_${typeId}`] === true;
			const industryName = props.dataItem.industryName || 'N/A';

			return (
				<td className="text-center">
					<SingleCheckbox
						id={`notif_${orgId}_${typeId}`}
						name={`notif_${orgId}_${typeId}`}
						checked={checked}
						className="table-checkbox-align"
						ariaLabel={`${industryName} - ${typeName}`}
						onChange={e => dispatch(toggleNotification({
							orgId,
							typeId,
							checked: e.target.checked
						}))}
					/>
				</td>
			);
		},
		[dispatch]
	);

	const allColumns = useMemo(
		(): ColumnField[] => [
			new ColumnField(
				'industryName', 
				FilterType.Text, 
				renderIndustryName, 
				false, 
				true, 
				false, 
				false, 
				{
					field: 'industryName',
					headerClassName: 'text-wrap',
					width: '180px'
				}
			),
			...allTypes.map(
				(t: any) =>
					new ColumnField(
						t.notificationType,
						FilterType.Boolean,
						renderCheckbox(t.emailNotificationTypeId, t.notificationType),
						false,
						undefined,
						undefined,
						false,
						{
							headerClassName: 'text-wrap text-center',
							width: '150px'
						}
					)
			)
		],
		[allTypes, renderIndustryName, renderCheckbox]
	);

	const defaultColumns = useMemo(
		() => ['industryName', ...allTypes.map((t: any) => t.notificationType)],
		[allTypes]
	);

	const gridOption: GridOption = useMemo(
		() => ({
			prefix: 'ipp.authorityAccount.authorityEmailListColumns',
			pageTitle: '',
			allColumns: allColumns,
			defaultColumns: defaultColumns,
			queryParameters: {
				userProfileId: String(userProfileId),
				page: String(initialPage),	
				size: String(shortPageSize)
			},
			showSearch: true,
			showAddButton: false,
			showActions: false,
			showEditColumnButton: false,
			showFilter: false,
			showTabList: false,
			showPageSizeMenu: true,
			pageSizeMenuPosition: 'bottom',
			pageSizeMenuLabel: localizationService.getLocalizedString('ipp.accountPortal.emailNotificationsPerPage'),
			rerenderOnQueryParamChange: true,
			rerenderOnGridOptionChange: true,
			storageName: LocalStorageName.IppUsersGrid
		}),
		[allColumns, defaultColumns, userProfileId]
	);

	const restState = useCallback((rootState: any) => {
		const s = selectEmailNotificationsState(rootState);
		return {
			loading: s.loading,
			error: s.error,
			result: s.data.result,
			page: s.data.page,
			size: s.data.size,
			total: s.data.total,
			selected: s.selected
		};
	}, []);

	const handleSave = useCallback(() => {
		if (!hasUnsavedChanges) return;

		const payload: Record<number, number[]> = {};
		
		fullDataset.forEach((org: any) => {
			const subscribedTypeIds = org.notificationTypes
				.filter((nt: any) => nt.isSubscribed)
				.map((nt: any) => nt.emailNotificationTypeId);
			payload[org.organizationId] = subscribedTypeIds;
		});

		dispatch(saveEmailNotificationSettings({ userProfileId, settings: payload }));
	}, [dispatch, userProfileId, fullDataset, hasUnsavedChanges]);

	return (
		<div className="accordion-data-grid">
			<CollapsibleCard
				className="mt-3"
				accordionType="invitation"
				accordionHeading={localizationService.getLocalizedString(
					'ipp.accountPortal.emailNotificationSettings'
				)}>
				<div className="email-notifications-grid">
					{error && (
						<div className="alert alert-danger" role="alert">
							{localizationService.getLocalizedString('ipp.errors.loadFailed')}
						</div>
					)}
					<DataGrid
						gridOption={gridOption}
						restSlice={emailNotificationsRestSlice}
						restState={restState}
						history={{} as any}
						match={{ params: {} } as any}
					/>
				</div>
				<div className="d-flex justify-content-end mt-3">
					<button 
						className="btn ai-save" 
						onClick={handleSave}
						disabled={!hasUnsavedChanges}
						title={
							!hasUnsavedChanges 
								? localizationService.getLocalizedString('ipp.tooltips.noChanges')
								: localizationService.getLocalizedString('ipp.buttons.save')
						}
					>
						{localizationService.getLocalizedString('ipp.buttons.save')}
					</button>
				</div>
			</CollapsibleCard>
		</div>
	);
};
